import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCountryOptions } from "../../../../../Hooks/useBaasCountryOptions";
import {
  BAAS_PERSONAL_WIZARD_STEPS,
  applyPersonLookupToPersonalForm,
  applySerproPersonDraftToPersonalForm,
  buildPersonalAccountPayload,
  clearPersonalEntidadeDraft,
  composeE164FromParts,
  createInitialPersonalCreateForm,
  createPersonalAccount,
  formatPersonalDocumentInput,
  formatPhonePartsForDisplay,
  getDefaultPersonalAccountPayloadJson,
  getPersonalLookupDocumentDigits,
  getPersonalStepCompletionStatus,
  hasPersonalIdentityDocNumber,
  isPhonePartsComplete,
  isValidPersonalCpf,
  maskPersonalDocumentNumber,
  normalizePersonalCreateForm,
  parsePersonalAccountPayloadJson,
  savePersonalEntidadeDraft,
  validatePersonalContactForEdit,
  validatePersonalFormForSubmit,
  validatePersonalFormStep,
} from "../../../../../Services/baasPersonalAccountApi";
import { lookupBaasPersonByDocument } from "../../../../../Services/baasUsersApi";
import { buildPhoneDialOptions } from "../../../../../Services/baasPhoneParts";
import {
  updateBaasEntityContact,
  updateBaasEntityDocument,
  upsertBaasEntityDocumentDeclaration,
  fetchBaasEntityById,
  autocompleteBaasEntity,
} from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceFeatures } from "../../../../../Services/baasInstancesApi";
import { formatBaasCreateConflictMessage } from "../../../../../Services/baasCreateConflictMessage";
import { formatBaasEntitySaveError, formatBaasEntityValidationMessage } from "../../../../../Services/baasEntityValidationMessage";
import { hasIdentityDocData, isBrCountry } from "../baasEntidadeCreateVisibility";
import {
  formatIdentityDocumentLabel,
  formatIdentityProofTitle,
  IDENTITY_DOC_LABEL_ROLE,
  IDENTITY_DOC_LABEL_VARIANT,
} from "../baasIdentityDocumentLabels";
import {
  nationalIdNumberIsFilled,
  resolveNationalIdScheme,
  shouldHidePassportForNationalId,
} from "../baasIdentitySchemes";
import {
  BrAddressFieldsGrid,
  CollapsibleSection,
  DebugConsoleFooter,
  Field,
  IdentityDocFields,
  PhonePartsFields,
  ReviewSection,
  SectionHeading,
  StepHeader,
  WizardStepTabs,
  inputClass,
} from "./baasEntidadeCreateFields";
import { BaasEntidadeDocumentsPanel } from "./BaasEntidadeDocumentsPanel";
import { useHistory } from "react-router-dom";

const buildPersonalEntityDetailPath = (entityId) =>
  `/baas/entidades/${encodeURIComponent(entityId)}`;

export const BAAS_ENTIDADE_PERSONAL_CREATE_STEPS = BAAS_PERSONAL_WIZARD_STEPS;

const formAlertClass =
  "mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-400/50 dark:bg-red-950/40 dark:text-red-100";

const resolvePersonalCreateConflictMessage = (error) => {
  const validationMessage = formatBaasEntityValidationMessage(error);
  if (validationMessage) {
    return validationMessage;
  }

  const code = String(error?.code || error?.data?.error || "").toLowerCase();
  const status = error?.status ?? error?.data?.status;
  const isConflict =
    status === 409 ||
    code === "entity_already_exists" ||
    code === "duplicate_request";

  if (!isConflict) {
    return intl.get("baas.entidades.personal.error");
  }

  const fieldErrors = error?.errors || error?.data?.errors;
  const fields =
    fieldErrors && typeof fieldErrors === "object" ? Object.keys(fieldErrors) : [];

  if (fields.length > 0) {
    const fieldLabels = fields
      .map((field) => {
        const key = `baas.entidades.conflict.field.${field}`;
        const label = intl.get(key);
        return label && label !== key ? label : field;
      })
      .join(", ");
    return intl.get("baas.entidades.conflict.alreadyExistsWithFields", { fields: fieldLabels });
  }

  return (
    formatBaasCreateConflictMessage(error, intl.get("baas.entidades.conflict.alreadyExists")) ||
    intl.get("baas.entidades.conflict.alreadyExists")
  );
};

const lookupBannerClass = (tone) => {
  if (tone === "success") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200";
  }
  if (tone === "warn") {
    return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100";
  }
  return "border-gray-200 bg-gray-50 text-gray-700 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-200";
};

const PersonalCreateWizardProgress = ({ steps, activeIndex, onStepClick, form, instanceId }) => (
  <nav aria-label={intl.get("baas.entidades.personal.stepNavigation")} className="mb-1">
    <ol className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-1">
      {steps.map((step, index) => {
        const status = getPersonalStepCompletionStatus(step, form, { instanceId });
        const isActive = index === activeIndex;
        const isComplete = status === "complete" && !isActive;
        const stepNumber = index + 1;

        return (
          <li key={step} className="flex min-w-0 items-center gap-1 sm:gap-1.5">
            {index > 0 ? (
              <span
                className={`hidden h-px w-4 shrink-0 sm:block ${
                  index <= activeIndex ? "bg-[#e7a73f]/60" : "bg-gray-200 dark:bg-brand-dark-border"
                }`}
                aria-hidden
              />
            ) : null}
            <button
              type="button"
              onClick={() => onStepClick(index)}
              aria-current={isActive ? "step" : undefined}
              className={`inline-flex max-w-full items-center gap-2 rounded-full px-2.5 py-1.5 text-left text-xs font-medium transition sm:px-3 ${
                isActive
                  ? "bg-[#e7a73f]/15 text-[#b8860b] ring-1 ring-[#e7a73f]/35"
                  : isComplete
                    ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-brand-dark-elevated dark:text-gray-400 dark:hover:bg-brand-dark-surface"
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                  isActive
                    ? "bg-[#e7a73f] text-white"
                    : isComplete
                      ? "bg-emerald-500 text-white"
                      : "bg-white text-gray-500 ring-1 ring-gray-200 dark:bg-brand-dark-surface dark:ring-brand-dark-border"
                }`}
              >
                {isComplete ? "✓" : stepNumber}
              </span>
              <span className="truncate">{intl.get(`baas.entidades.personal.step.${step}`)}</span>
            </button>
          </li>
        );
      })}
    </ol>
    <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
      {intl.get("baas.entidades.personal.progressOf", {
        current: activeIndex + 1,
        total: steps.length,
      })}
    </p>
  </nav>
);

const LookupResultBanner = ({ notice, tone, existingEntityId, onOpenEntity }) => {
  if (!notice) {
    return null;
  }

  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${lookupBannerClass(tone)}`} role="status">
      <p className="font-medium">{notice}</p>
      {existingEntityId ? (
        <button type="button" className="btn-outline mt-3" onClick={onOpenEntity}>
          {intl.get("baas.entidades.personal.lookup.openEntity")}
        </button>
      ) : null}
    </div>
  );
};

const LookupSkeleton = () => (
  <div
    className="space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface"
    aria-busy="true"
    aria-live="polite"
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {intl.get("baas.entidades.personal.lookup.lookingUp")}
    </p>
    <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
    <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
    <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
  </div>
);

const formatPersonalAddressForReview = (address) => {
  if (!address?.line1?.trim()) {
    return "—";
  }
  return `${address.line1}, ${address.number}, ${address.neighborhood} — ${address.city}, ${address.state} ${address.postalCode} · ${address.countryCode}`;
};

const formatPersonalIdentityDocForReview = (doc) => {
  if (!hasPersonalIdentityDocNumber(doc)) {
    return null;
  }

  const parts = [maskPersonalDocumentNumber(doc.number), doc.countryCode || "—"];

  if (doc.expirationDate) {
    parts.push(doc.expirationDate);
  }
  if (doc.issuingRegion?.trim()) {
    parts.push(doc.issuingRegion.trim());
  }

  return parts.join(" · ");
};

export const BaasEntidadePersonalCreateForm = ({
  active = true,
  debugConsoleExpanded = false,
  onCreated,
  onUpdated,
  readOnly = false,
  /** When true, lock identity/document fields and allow phone + address save. */
  editMode = false,
  unlockIdentity = false,
  entityId = null,
  initialForm = null,
  fixedInstanceId = null,
  onCancel,
  cancelLabel,
  documents = null,
  documentsUploadEnabled = false,
  allowSkipKyc = null,
  onDocumentsUploaded,
  onDocumentUpdated,
  verificationEntity = null,
  hideDocumentsPanel = false,
}) => {
  const history = useHistory();
  const { instances, selectedInstance } = useBaasInstance();
  const [stepIndex, setStepIndex] = useState(0);
  const [instanceId, setInstanceId] = useState("");
  const activeInstance =
    instances.find((item) => item.id === instanceId) || selectedInstance || instances[0];
  const { countryOptions } = useBaasCountryOptions(activeInstance);
  const phoneDialOptions = buildPhoneDialOptions(countryOptions);
  const [form, setForm] = useState(createInitialPersonalCreateForm);
  const [debugExpanded, setDebugExpanded] = useState(debugConsoleExpanded);
  const [jsonText, setJsonText] = useState("");
  const [debugError, setDebugError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupNotice, setLookupNotice] = useState("");
  const [lookupNoticeTone, setLookupNoticeTone] = useState("info");
  const [existingEntityId, setExistingEntityId] = useState(null);
  const [usePassportDoc, setUsePassportDoc] = useState(false);
  const [error, setError] = useState("");
  const [validationIssues, setValidationIssues] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [serproAutocompleteEnabled, setSerproAutocompleteEnabled] = useState(false);

  const isViewOnly = readOnly && !editMode;
  const isEditableDetail = Boolean(editMode);
  const isCreateFlow = !readOnly && !editMode;
  // Core person name/DOB stay locked on detail unless Valut super-admin unlocks identity.
  const corePersonFieldsLocked = isViewOnly || (isEditableDetail && !unlockIdentity);
  const identityDocsLocked = isViewOnly;
  const identityLocked = corePersonFieldsLocked;

  const cpfDigits = useMemo(
    () => String(form.nationalId?.number ?? "").replace(/\D/g, ""),
    [form.nationalId?.number],
  );
  const cpfIsValid = isValidPersonalCpf(cpfDigits);
  const hasStandaloneDriversLicense =
    hasIdentityDocData(form.driversLicense) && !nationalIdNumberIsFilled(form.nationalId);
  const hasForeignPrimaryDl =
    hasIdentityDocData(form.driversLicense)
    && form.driversLicense?.countryCode
    && !isBrCountry(form.driversLicense.countryCode);
  const isBrCpfOnPersonalData =
    nationalIdNumberIsFilled(form.nationalId)
    && isBrCountry(form.nationalId?.countryCode || "BR");
  const nationalIdScheme = resolveNationalIdScheme(
    nationalIdNumberIsFilled(form.nationalId)
      ? form.nationalId?.countryCode || "BR"
      : hasStandaloneDriversLicense
        ? form.driversLicense?.countryCode || null
        : form.nationalId?.countryCode || "BR",
  );
  const hidePassportPath = shouldHidePassportForNationalId(form.nationalId);
  const passportReady = Boolean(String(form.passport?.number ?? "").trim());
  const documentContinueEnabled =
    !isCreateFlow || (usePassportDoc ? passportReady : cpfIsValid);

  useEffect(() => {
    let cancelled = false;
    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance?.baasInstanceId || !isCreateFlow) {
      setSerproAutocompleteEnabled(false);
      return undefined;
    }
    fetchBaasInstanceFeatures(instance)
      .then((features) => {
        if (cancelled) return;
        const list = Array.isArray(features) ? features : [];
        const row = list.find(
          (item) =>
            String(item?.featureCode || "")
              .trim()
              .toLowerCase() === "serpro.autocomplete",
        );
        setSerproAutocompleteEnabled(Boolean(row?.enabled));
      })
      .catch(() => {
        if (!cancelled) setSerproAutocompleteEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId, selectedInstance, activeInstance, instances, isCreateFlow]);

  useEffect(() => {
    if (!active) {
      return;
    }

    const initialInstanceId =
      fixedInstanceId || selectedInstance?.id || instances[0]?.id || "";

    if (!readOnly && !editMode) {
      clearPersonalEntidadeDraft(initialInstanceId);
    }

    setStepIndex(0);
    setForm(
      normalizePersonalCreateForm(
        (readOnly || editMode) && initialForm ? initialForm : createInitialPersonalCreateForm(),
      ),
    );
    setInstanceId(initialInstanceId);
    setDebugExpanded(readOnly || editMode ? false : debugConsoleExpanded);
    setJsonText(getDefaultPersonalAccountPayloadJson());
    setDebugError("");
    setError("");
    setValidationIssues([]);
    setSaveSuccess("");
    setLookupNotice("");
    setLookupNoticeTone("info");
    setExistingEntityId(null);
    setUsePassportDoc(false);
    setLookingUp(false);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on create/view entry / instance switch
  }, [active, selectedInstance?.id, debugConsoleExpanded, readOnly, editMode, initialForm, fixedInstanceId]);

  useEffect(() => {
    if (!active || !instanceId || readOnly || editMode) {
      return;
    }
    savePersonalEntidadeDraft(instanceId, { form, stepIndex });
  }, [active, instanceId, form, stepIndex, readOnly, editMode]);

  const currentStep = BAAS_PERSONAL_WIZARD_STEPS[stepIndex];
  const isLastStep = stepIndex === BAAS_PERSONAL_WIZARD_STEPS.length - 1;

  const validateStep = () => {
    if (isEditableDetail) {
      if (currentStep === "personalData" && !isPhonePartsComplete(form.phone)) {
        setError(intl.get("baas.entidades.personal.phoneRequired"));
        return false;
      }
      if (currentStep === "address") {
        const contactValidation = validatePersonalContactForEdit(form);
        if (!contactValidation.valid && contactValidation.errorKey?.includes("address")) {
          setError(intl.get(contactValidation.errorKey));
          return false;
        }
      }
      setError("");
      return true;
    }

    const result = validatePersonalFormStep(currentStep, form, { instanceId });

    if (!result.valid) {
      const errorKey = result.errorKey ?? "baas.entidades.personal.incompleteRequiredFields";
      setError(intl.get(errorKey));
      setValidationIssues([{ step: currentStep, errorKey }]);
      return false;
    }

    setError("");
    setValidationIssues([]);
    return true;
  };

  const runDocumentLookup = async () => {
    const digits = getPersonalLookupDocumentDigits(form);
    if (digits.length !== 11 && digits.length !== 14) {
      // Passport / non-CPF docs: skip lookup and continue empty.
      setLookupNotice("");
      setExistingEntityId(null);
      return true;
    }

    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    setLookingUp(true);
    try {
      const result = await lookupBaasPersonByDocument(digits, {
        instanceId: instance?.baasInstanceId ?? instance?.id,
      });

      if (!result.found) {
        setLookupNotice(intl.get("baas.entidades.personal.lookup.notFound"));
        setLookupNoticeTone("info");
        setExistingEntityId(null);
        return true;
      }

      if (result.hasPersonEntity && result.person?.entityId) {
        setExistingEntityId(result.person.entityId);
        setForm((current) => applyPersonLookupToPersonalForm(current, result.person));
        setLookupNotice(intl.get("baas.entidades.personal.lookup.alreadyEntity"));
        setLookupNoticeTone("warn");
        return false;
      }

      setExistingEntityId(null);
      setForm((current) => applyPersonLookupToPersonalForm(current, result.person));
      setLookupNotice(intl.get("baas.entidades.personal.lookup.found"));
      setLookupNoticeTone("success");
      return true;
    } catch {
      setError(intl.get("baas.entidades.personal.lookup.failed"));
      return false;
    } finally {
      setLookingUp(false);
    }
  };

  const handleNext = async () => {
    if (!isViewOnly && isCreateFlow && currentStep === "document" && !documentContinueEnabled) {
      setError(
        intl.get(
          usePassportDoc
            ? "baas.entidades.personal.documentRequired"
            : "baas.entidades.personal.cpfInvalid",
        ),
      );
      return;
    }

    if (!isViewOnly && !validateStep()) {
      return;
    }

    if (!isViewOnly && !isEditableDetail && currentStep === "document") {
      const canContinue = await runDocumentLookup();
      if (!canContinue) {
        return;
      }
    }

    if (
      !isViewOnly
      && isCreateFlow
      && currentStep === "personalData"
      && serproAutocompleteEnabled
      && !usePassportDoc
      && cpfIsValid
      && String(form.dateOfBirth || "").trim()
    ) {
      const instance =
        instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
      if (instance) {
        setLookingUp(true);
        try {
          const auto = await autocompleteBaasEntity(instance, {
            document: cpfDigits,
            dateOfBirth: String(form.dateOfBirth).trim().slice(0, 10),
          });
          if (auto?.draft) {
            setForm((current) => applySerproPersonDraftToPersonalForm(current, auto.draft));
            setLookupNotice(
              intl.get("baas.entidades.personal.lookup.serproFilled")
                || "Nome preenchido via SERPRO. Complete endereço e demais campos.",
            );
            setLookupNoticeTone("success");
          }
        } catch {
          // Keep manual flow when SERPRO fails.
        } finally {
          setLookingUp(false);
        }
      }
    }

    setError("");
    setStepIndex((current) => Math.min(current + 1, BAAS_PERSONAL_WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setError("");
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const handleStepClick = (index) => {
    setError("");
    setStepIndex(index);
  };

  const updatePersonalDoc = (docKey, patch) => {
    // Must mirror IdentityDocFields `disabled={identityDocsLocked}` — not identityLocked
    // (name/DOB). Using identityLocked here swallowed typing in admin editMode.
    if (identityDocsLocked) {
      return;
    }
    setForm((current) => ({
      ...current,
      [docKey]: { ...current[docKey], ...patch },
    }));
  };

  const handleInstanceChange = (nextInstanceId) => {
    if (instanceId && nextInstanceId !== instanceId) {
      clearPersonalEntidadeDraft(instanceId);
    }

    clearPersonalEntidadeDraft(nextInstanceId);
    setInstanceId(nextInstanceId);
    setForm(normalizePersonalCreateForm(createInitialPersonalCreateForm()));
    setStepIndex(0);
    setLookupNotice("");
    setLookupNoticeTone("info");
    setExistingEntityId(null);
    setUsePassportDoc(false);
    setError("");
  };

  const clearLookupState = () => {
    setLookupNotice("");
    setLookupNoticeTone("info");
    setExistingEntityId(null);
  };

  const handleCpfChange = (rawValue) => {
    clearLookupState();
    setError("");
    setForm((current) => ({
      ...current,
      nationalId: {
        ...current.nationalId,
        number: formatPersonalDocumentInput(rawValue),
        countryCode: current.nationalId?.countryCode || "BR",
      },
      // CPF path owns the primary identity — clear passport so validation stays single-doc.
      passport: usePassportDoc
        ? current.passport
        : { id: "", number: "", countryCode: "", expirationDate: "", issuingRegion: "" },
    }));
  };

  const handleTogglePassportPath = (enabled) => {
    clearLookupState();
    setError("");
    setUsePassportDoc(enabled);
    setForm((current) =>
      normalizePersonalCreateForm({
        ...current,
        nationalId: enabled
          ? { id: "", number: "", countryCode: "BR", expirationDate: "", issuingRegion: "" }
          : { ...current.nationalId, countryCode: current.nationalId?.countryCode || "BR" },
        passport: enabled
          ? current.passport
          : { id: "", number: "", countryCode: "", expirationDate: "", issuingRegion: "" },
        driversLicense: { id: "", number: "", countryCode: "", expirationDate: "", issuingRegion: "" },
      }),
    );
  };

  const goToStep = (stepKey) => {
    const index = BAAS_PERSONAL_WIZARD_STEPS.indexOf(stepKey);
    if (index >= 0) {
      setError("");
      setStepIndex(index);
    }
  };

  const handleSyncJsonFromForm = () => {
    try {
      const payload = buildPersonalAccountPayload(form);
      setJsonText(JSON.stringify(payload, null, 2));
      setDebugError("");
    } catch {
      setDebugError(intl.get("baas.entidades.personal.jsonFormatError"));
    }
  };

  const handleFormatJson = () => {
    try {
      setJsonText(JSON.stringify(JSON.parse(jsonText), null, 2));
      setDebugError("");
    } catch {
      setDebugError(intl.get("baas.entidades.personal.jsonFormatError"));
    }
  };

  const handleRestoreTemplate = () => {
    setJsonText(getDefaultPersonalAccountPayloadJson());
    setDebugError("");
  };

  const submitPayload = async (payload) => {
    if (!instanceId) {
      setError(intl.get("baas.entidades.personal.instanceRequired"));
      return;
    }

    if (existingEntityId) {
      setError(intl.get("baas.entidades.personal.lookup.alreadyEntity"));
      return;
    }

    setSubmitting(true);
    setError("");
    setValidationIssues([]);
    setDebugError("");

    try {
      const instance =
        instances.find((item) => item.id === instanceId) || selectedInstance;
      const result = await createPersonalAccount(instance, payload);
      clearPersonalEntidadeDraft(instanceId);
      const createdEntity = result?.entity ?? result;
      onCreated?.(createdEntity);
    } catch (err) {
      const conflictMessage = resolvePersonalCreateConflictMessage(err);
      setError(conflictMessage);

      const code = String(err?.code || err?.data?.error || "").toLowerCase();
      if ((err?.status === 409 || code === "entity_already_exists") && !existingEntityId) {
        const digits = getPersonalLookupDocumentDigits(form);
        if (digits.length === 11 || digits.length === 14) {
          try {
            const instance =
              instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
            const lookup = await lookupBaasPersonByDocument(digits, {
              instanceId: instance?.baasInstanceId ?? instance?.id,
            });
            if (lookup.hasPersonEntity && lookup.person?.entityId) {
              setExistingEntityId(lookup.person.entityId);
              setLookupNotice(intl.get("baas.entidades.personal.lookup.alreadyEntity"));
              setLookupNoticeTone("warn");
            }
          } catch {
            // Keep conflict message only.
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDebugSubmit = async () => {
    if (!instanceId) {
      setDebugError(intl.get("baas.entidades.personal.instanceRequired"));
      return;
    }

    try {
      const payload = parsePersonalAccountPayloadJson(jsonText);
      await submitPayload(payload);
    } catch {
      setDebugError(intl.get("baas.entidades.personal.jsonInvalid"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isEditableDetail) {
      await handleSaveContact();
      return;
    }

    // Revisão is preview-only. Never create from tab click / Enter / button reuse.
    if (!isLastStep) {
      return;
    }

    if (existingEntityId) {
      setError(intl.get("baas.entidades.personal.lookup.alreadyEntity"));
      return;
    }

    const submitValidation = validatePersonalFormForSubmit(form, { instanceId });

    if (!submitValidation.valid) {
      setValidationIssues(submitValidation.issues || []);
      setError(intl.get("baas.entidades.personal.formIncompleteAlert"));
      return;
    }

    if (!instanceId) {
      setError(intl.get("baas.entidades.personal.instanceRequired"));
      return;
    }

    await submitPayload(buildPersonalAccountPayload(form));
  };

  const handleSaveContact = async () => {
    const contactValidation = validatePersonalContactForEdit(form);
    if (!contactValidation.valid) {
      setError(intl.get(contactValidation.errorKey ?? "baas.entidades.personal.incompleteRequiredFields"));
      return;
    }

    if (!entityId) {
      setError(intl.get("baas.entidades.detail.saveError"));
      return;
    }

    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance) {
      setError(intl.get("baas.entidades.personal.instanceRequired"));
      return;
    }

    setSubmitting(true);
    setError("");
    setSaveSuccess("");

    try {
      const identityPatch = {};
      if (unlockIdentity) {
        const nextFirst = String(form.firstName || "").trim();
        const nextLast = String(form.lastName || "").trim();
        const nextMiddle = String(form.middleName || "").trim();
        const nextDob = String(form.dateOfBirth || "").trim();
        const nextMother = String(form.motherName || "").trim();
        const prevFirst = String(initialForm?.firstName || "").trim();
        const prevLast = String(initialForm?.lastName || "").trim();
        const prevMiddle = String(initialForm?.middleName || "").trim();
        const prevDob = String(initialForm?.dateOfBirth || "").trim();
        const prevMother = String(initialForm?.motherName || "").trim();
        const changed =
          nextFirst.toLowerCase() !== prevFirst.toLowerCase()
          || nextLast.toLowerCase() !== prevLast.toLowerCase()
          || nextMiddle.toLowerCase() !== prevMiddle.toLowerCase()
          || nextDob !== prevDob
          || nextMother.toLowerCase() !== prevMother.toLowerCase();
        if (changed) {
          identityPatch.first_name = nextFirst;
          identityPatch.last_name = nextLast;
          identityPatch.middle_name = nextMiddle || null;
          identityPatch.date_of_birth = nextDob || null;
          identityPatch.mother_name = nextMother || null;
        }
      }
      const updated = await updateBaasEntityContact(instance, entityId, {
        phone: form.phone,
        phoneNumber: composeE164FromParts(form.phone),
        email: form.email?.trim() || null,
        address: form.address,
        ...identityPatch,
      });

      const identityDocs = [
        { type: "PASSPORT", field: "passport", doc: form.passport },
        { type: "DRIVERS_LICENSE", field: "driversLicense", doc: form.driversLicense },
        { type: "NATIONAL_ID", field: "nationalId", doc: form.nationalId },
      ];
      const identityPatches = {};
      for (const item of identityDocs) {
        const number = String(item.doc?.number || "").trim();
        const countryCode = String(item.doc?.countryCode || "").trim().toUpperCase();
        if (!number && !countryCode) continue;
        if (
          item.type === "DRIVERS_LICENSE"
          && isBrCpfOnPersonalData
          && countryCode
          && !isBrCountry(countryCode)
        ) {
          continue;
        }
        const patch = {
          number: number || undefined,
          countryCode: countryCode || undefined,
          issuingRegion: item.doc?.issuingRegion || undefined,
          expirationDate: item.doc?.expirationDate || undefined,
        };
        const saved = item.doc?.id
          ? await updateBaasEntityDocument(instance, entityId, item.doc.id, patch)
          : await upsertBaasEntityDocumentDeclaration(instance, entityId, {
              type: item.type,
              ...patch,
            });
        const savedId = String(saved?.id ?? saved?.Id ?? "").trim();
        if (savedId) {
          identityPatches[item.field] = { ...item.doc, ...patch, id: savedId };
        }
      }
      if (Object.keys(identityPatches).length > 0) {
        setForm((current) => normalizePersonalCreateForm({ ...current, ...identityPatches }));
      }

      // Re-GET so contact (email/phone parts) rehydrates from persisted AccountPersonPhone.
      let refreshed = updated;
      try {
        refreshed = (await fetchBaasEntityById(instance, entityId)) || updated;
      } catch {
        refreshed = updated;
      }

      setSaveSuccess(intl.get("baas.entidades.detail.saveSuccess"));
      onUpdated?.(refreshed);
    } catch (err) {
      setError(
        formatBaasEntitySaveError(err, intl.get("baas.entidades.detail.saveError")),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderInstanceSelector = () => (
    <Field label={intl.get("baas.entidades.columns.instance")} required={!identityLocked}>
      <select
        className={inputClass}
        value={instanceId}
        onChange={(event) => handleInstanceChange(event.target.value)}
        required={!identityLocked}
        disabled={identityLocked}
      >
        <option value="" disabled>
          {intl.get("baas.entidades.personal.selectInstance")}
        </option>
        {instances.map((instance) => (
          <option key={instance.id} value={instance.id}>
            {instance.name}
          </option>
        ))}
      </select>
    </Field>
  );

  const renderNationalIdPersonalDataSection = () => {
    if (hasStandaloneDriversLicense) return null;
    if (isCreateFlow && usePassportDoc) return null;

    return (
      <div className="space-y-4">
        <SectionHeading>
          {formatIdentityDocumentLabel({
            documentType: "NATIONAL_ID",
            countryCode: form.nationalId?.countryCode || "BR",
            countryOptions,
            labelRole: IDENTITY_DOC_LABEL_ROLE.DECLARATION,
          })}
        </SectionHeading>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.personal.nationalIdPersonalDataHint")}
        </p>
        {hasForeignPrimaryDl ? (
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            {intl.get("baas.entidades.detail.brCpfForeignDlPersonalDataHint")}
          </p>
        ) : null}
        <IdentityDocFields
          doc={form.nationalId}
          onChange={(patch) => updatePersonalDoc("nationalId", patch)}
          compact
          countryOptions={countryOptions}
          disabled={identityDocsLocked}
          forcedCountryCode="BR"
        />
      </div>
    );
  };

  const renderIdentityDocumentsAccordion = ({ showSectionHeading = false } = {}) => (
    <>
      {showSectionHeading ? (
        <SectionHeading>{intl.get("baas.entidades.personal.section.document")}</SectionHeading>
      ) : null}
      {isEditableDetail ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.editIdentityDocsHint")}
        </p>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.personal.documentStepHint")}
        </p>
      )}
      <div className="space-y-3">
        {shouldHidePassportForNationalId(form.nationalId) ? null : (
        <CollapsibleSection
          title={formatIdentityDocumentLabel({
            documentType: "PASSPORT",
            countryCode: form.passport?.countryCode,
            countryOptions,
          })}
          optional
          defaultOpen={hasIdentityDocData(form.passport)}
        >
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.detail.documentsPassportPathHint")}
          </p>
          <IdentityDocFields
            doc={form.passport}
            onChange={(patch) => updatePersonalDoc("passport", patch)}
            compact
            countryOptions={countryOptions}
            disabled={identityDocsLocked}
          />
        </CollapsibleSection>
        )}
        {hasStandaloneDriversLicense ? (
        <CollapsibleSection
          title={formatIdentityDocumentLabel({
            documentType: "DRIVERS_LICENSE",
            countryCode: form.driversLicense?.countryCode,
            countryOptions,
            variant: IDENTITY_DOC_LABEL_VARIANT.STANDALONE,
          })}
          optional
          defaultOpen
        >
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.detail.documentsDriversLicensePathHint")}
          </p>
          <IdentityDocFields
            doc={form.driversLicense}
            onChange={(patch) => updatePersonalDoc("driversLicense", patch)}
            compact
            countryOptions={countryOptions}
            disabled={identityDocsLocked}
          />
        </CollapsibleSection>
        ) : (
        <CollapsibleSection
          title={formatIdentityDocumentLabel({
            documentType: "NATIONAL_ID",
            countryCode: form.nationalId?.countryCode || "BR",
            countryOptions,
          })}
          optional
          defaultOpen={
            !entityId ||
            hasIdentityDocData(form.nationalId) ||
            hasIdentityDocData(form.driversLicense)
          }
        >
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {intl.get(nationalIdScheme.locale.pathHintKey)}
          </p>
          <IdentityDocFields
            doc={form.nationalId}
            onChange={(patch) => updatePersonalDoc("nationalId", patch)}
            compact
            countryOptions={countryOptions}
            disabled={identityDocsLocked}
          />
          {nationalIdScheme.proofKind === "drivers_license" ? (
          <div className="mt-4 rounded-lg border border-dashed border-gray-200 p-3 dark:border-brand-dark-border">
            <p className="text-sm font-medium text-brand-dark dark:text-gray-100">
              {formatIdentityProofTitle({
                documentType: "NATIONAL_ID",
                countryCode: form.nationalId?.countryCode || "BR",
                countryOptions,
              })}
            </p>
            <p className="mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
              {intl.get(nationalIdScheme.locale.proofHintKey)}
            </p>
            <IdentityDocFields
              doc={form.driversLicense}
              onChange={(patch) => updatePersonalDoc("driversLicense", patch)}
              compact
              countryOptions={countryOptions}
              disabled={identityDocsLocked}
              forcedCountryCode={
                isBrCountry(form.nationalId?.countryCode) ? "BR" : null
              }
            />
          </div>
          ) : null}
        </CollapsibleSection>
        )}
      </div>
    </>
  );

  const renderCreateDocumentStep = () => (
    <div className="space-y-5">
      <StepHeader
        title={intl.get("baas.entidades.personal.step.document")}
        description={intl.get("baas.entidades.personal.stepDesc.document")}
      />

      {!selectedInstance && !identityLocked ? (
        <div className="max-w-md">{renderInstanceSelector()}</div>
      ) : null}

      {!usePassportDoc ? (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <Field label={intl.get("baas.entidades.personal.cpfLabel")} required>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              className={`${inputClass} h-12 text-base tracking-wide sm:text-lg`}
              value={form.nationalId?.number || ""}
              onChange={(event) => handleCpfChange(event.target.value)}
              placeholder={intl.get("baas.entidades.personal.cpfPlaceholder")}
              disabled={lookingUp || submitting}
              aria-invalid={cpfDigits.length === 11 && !cpfIsValid ? "true" : undefined}
            />
          </Field>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.personal.cpfFieldHint")}
          </p>
          {cpfDigits.length === 11 && !cpfIsValid ? (
            <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
              {intl.get("baas.entidades.personal.cpfInvalid")}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3 rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.personal.passportHint")}
          </p>
          <IdentityDocFields
            doc={form.passport}
            onChange={(patch) => {
              clearLookupState();
              updatePersonalDoc("passport", patch);
            }}
            compact
            countryOptions={countryOptions}
            disabled={lookingUp || submitting}
          />
        </div>
      )}

      {hidePassportPath && !usePassportDoc ? null : (
      <button
        type="button"
        className="text-sm font-medium text-[#b8860b] underline-offset-2 hover:underline"
        onClick={() => handleTogglePassportPath(!usePassportDoc)}
        disabled={lookingUp || submitting}
      >
        {usePassportDoc
          ? intl.get("baas.entidades.personal.useCpfInstead")
          : intl.get("baas.entidades.personal.usePassport")}
      </button>
      )}

      {lookingUp ? <LookupSkeleton /> : null}

      {!lookingUp ? (
        <LookupResultBanner
          notice={lookupNotice}
          tone={lookupNoticeTone}
          existingEntityId={existingEntityId}
          onOpenEntity={() => history.push(buildPersonalEntityDetailPath(existingEntityId))}
        />
      ) : null}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case "document":
        if (isCreateFlow) {
          return renderCreateDocumentStep();
        }

        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.personal.step.document")}
              description={
                isEditableDetail
                  ? intl.get("baas.entidades.detail.documentEditHint")
                  : intl.get("baas.entidades.personal.stepDesc.document")
              }
            />
            {!selectedInstance && !identityLocked ? (
              <>
                <SectionHeading>{intl.get("baas.entidades.personal.section.context")}</SectionHeading>
                {renderInstanceSelector()}
              </>
            ) : null}
            {!entityId ? renderIdentityDocumentsAccordion({ showSectionHeading: true }) : null}
            {lookupNotice && !isViewOnly && !isEditableDetail ? (
              <LookupResultBanner
                notice={lookupNotice}
                tone={lookupNoticeTone}
                existingEntityId={existingEntityId}
                onOpenEntity={() => history.push(buildPersonalEntityDetailPath(existingEntityId))}
              />
            ) : null}
          </>
        );

      case "personalData":
        return (
          <div className="space-y-5">
            <StepHeader
              title={intl.get("baas.entidades.personal.step.personalData")}
              description={
                isEditableDetail
                  ? intl.get("baas.entidades.detail.editPersonalHint")
                  : intl.get("baas.entidades.personal.stepDesc.personalData")
              }
            />
            {isCreateFlow && lookupNoticeTone === "success" && lookupNotice ? (
              <LookupResultBanner notice={lookupNotice} tone="success" />
            ) : null}
            {isCreateFlow && lookupNoticeTone === "info" && lookupNotice ? (
              <LookupResultBanner notice={lookupNotice} tone="info" />
            ) : null}

            <div className="space-y-4">
              <SectionHeading>{intl.get("baas.entidades.personal.section.identity")}</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={intl.get("baas.entidades.personal.firstName")} required>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.firstName}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, firstName: event.target.value }))
                    }
                    required
                    disabled={identityLocked}
                    readOnly={identityLocked}
                  />
                </Field>
                <Field label={intl.get("baas.entidades.personal.lastName")} required>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.lastName}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, lastName: event.target.value }))
                    }
                    required
                    disabled={identityLocked}
                    readOnly={identityLocked}
                  />
                </Field>
                <Field label={intl.get("baas.entidades.personal.dateOfBirth")} required>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.dateOfBirth}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, dateOfBirth: event.target.value }))
                    }
                    required
                    disabled={identityLocked}
                    readOnly={identityLocked}
                  />
                </Field>
                <Field label={intl.get("baas.entidades.personal.motherName")} required>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.motherName}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, motherName: event.target.value }))
                    }
                    required
                    disabled={identityLocked}
                    readOnly={identityLocked}
                  />
                </Field>
              </div>
            </div>

            {renderNationalIdPersonalDataSection()}

            <div className="space-y-4">
              <SectionHeading>{intl.get("baas.entidades.personal.section.contact")}</SectionHeading>
              <Field label={intl.get("baas.entidades.personal.email")} required>
                <input
                  type="email"
                  className={inputClass}
                  value={form.email}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, email: event.target.value }))
                  }
                  required
                  disabled={identityLocked}
                  readOnly={identityLocked}
                />
              </Field>
              <PhonePartsFields
                value={form.phone}
                onChange={(patch) =>
                  setForm((current) => ({
                    ...current,
                    phone: { ...current.phone, ...patch },
                  }))
                }
                dialOptions={phoneDialOptions}
                required
                disabled={isViewOnly}
                readOnly={isViewOnly}
              />
            </div>
          </div>
        );

      case "address":
        return (
          <div className="space-y-4">
            <StepHeader
              title={intl.get("baas.entidades.personal.step.address")}
              description={intl.get("baas.entidades.personal.stepDesc.address")}
            />
            {isViewOnly ? (
              <div className="pointer-events-none opacity-95">
                <BrAddressFieldsGrid
                  address={form.address}
                  onChange={() => {}}
                  required
                  countryOptions={countryOptions}
                />
              </div>
            ) : (
              <BrAddressFieldsGrid
                address={form.address}
                onChange={(patch) =>
                  setForm((current) => ({
                    ...current,
                    address: { ...current.address, ...patch },
                  }))
                }
                required
                countryOptions={countryOptions}
              />
            )}
          </div>
        );

      case "review":
        return (
          <div className="space-y-4">
            <StepHeader
              title={intl.get("baas.entidades.personal.step.review")}
              description={intl.get("baas.entidades.personal.stepDesc.review")}
            />

            <div
              className={
                isCreateFlow
                  ? "space-y-4 rounded-xl border border-gray-100 bg-gray-50/70 p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface"
                  : "space-y-3"
              }
            >
              <ReviewSection title={intl.get("baas.entidades.personal.step.document")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    {formatPersonalIdentityDocForReview(form.nationalId) ? (
                      <p>
                        {formatIdentityDocumentLabel({
                          documentType: "NATIONAL_ID",
                          countryCode: form.nationalId?.countryCode || "BR",
                          countryOptions,
                        })}
                        {": "}
                        {formatPersonalIdentityDocForReview(form.nationalId)}
                      </p>
                    ) : null}
                    {formatPersonalIdentityDocForReview(form.passport) ? (
                      <p>
                        {formatIdentityDocumentLabel({
                          documentType: "PASSPORT",
                          countryCode: form.passport?.countryCode,
                          countryOptions,
                        })}
                        {": "}
                        {formatPersonalIdentityDocForReview(form.passport)}
                      </p>
                    ) : null}
                    {formatPersonalIdentityDocForReview(form.driversLicense) ? (
                      <p>
                        {formatIdentityDocumentLabel({
                          documentType: "DRIVERS_LICENSE",
                          countryCode: form.driversLicense?.countryCode,
                          countryOptions,
                          variant: nationalIdNumberIsFilled(form.nationalId)
                            ? IDENTITY_DOC_LABEL_VARIANT.CPF_PROOF
                            : IDENTITY_DOC_LABEL_VARIANT.STANDALONE,
                        })}
                        {": "}
                        {formatPersonalIdentityDocForReview(form.driversLicense)}
                      </p>
                    ) : null}
                    {!formatPersonalIdentityDocForReview(form.nationalId) &&
                    !formatPersonalIdentityDocForReview(form.passport) &&
                    !formatPersonalIdentityDocForReview(form.driversLicense) ? (
                      <p>—</p>
                    ) : null}
                  </div>
                  {isCreateFlow ? (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-[#b8860b] hover:underline"
                      onClick={() => goToStep("document")}
                    >
                      {intl.get("baas.entidades.personal.editStep")}
                    </button>
                  ) : null}
                </div>
              </ReviewSection>

              <ReviewSection title={intl.get("baas.entidades.personal.step.personalData")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium text-brand-dark dark:text-gray-100">
                      {form.firstName} {form.lastName}
                    </p>
                    <p>{form.dateOfBirth || "—"}</p>
                    <p>{form.motherName || "—"}</p>
                    <p>{form.email || "—"}</p>
                    <p>{formatPhonePartsForDisplay(form.phone)}</p>
                  </div>
                  {isCreateFlow ? (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-[#b8860b] hover:underline"
                      onClick={() => goToStep("personalData")}
                    >
                      {intl.get("baas.entidades.personal.editStep")}
                    </button>
                  ) : null}
                </div>
              </ReviewSection>

              <ReviewSection title={intl.get("baas.entidades.personal.step.address")}>
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0">{formatPersonalAddressForReview(form.address)}</p>
                  {isCreateFlow ? (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-[#b8860b] hover:underline"
                      onClick={() => goToStep("address")}
                    >
                      {intl.get("baas.entidades.personal.editStep")}
                    </button>
                  ) : null}
                </div>
              </ReviewSection>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isEditableDetail
                ? intl.get("baas.entidades.detail.reviewEditHint")
                : intl.get("baas.entidades.personal.reviewHint")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const createNextDisabled =
    submitting ||
    lookingUp ||
    (isCreateFlow && currentStep === "document" && !documentContinueEnabled);

  return (
    <form
      onSubmit={isViewOnly ? (event) => event.preventDefault() : handleSubmit}
      onKeyDown={
        isCreateFlow && !isLastStep
          ? (event) => {
              if (event.key !== "Enter") {
                return;
              }
              const target = event.target;
              if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) {
                return;
              }
              if (target.type === "textarea" || target.tagName === "TEXTAREA") {
                return;
              }
              event.preventDefault();
              if (!createNextDisabled) {
                handleNext();
              }
            }
          : undefined
      }
      className={`baas-entidade-create-form app-card ${isCreateFlow ? "mt-5 " : ""}p-6${isViewOnly ? " baas-entidade-create-form--readonly" : ""}`}
      data-debug-expanded={debugExpanded ? "true" : "false"}
      data-readonly={isViewOnly ? "true" : "false"}
      data-edit-mode={isEditableDetail ? "true" : "false"}
    >
      <div className="mb-6 border-b border-gray-100 pb-5 dark:border-brand-dark-border">
        {isCreateFlow ? (
          <PersonalCreateWizardProgress
            steps={BAAS_PERSONAL_WIZARD_STEPS}
            activeIndex={stepIndex}
            onStepClick={handleStepClick}
            form={form}
            instanceId={instanceId}
          />
        ) : (
          <WizardStepTabs
            steps={BAAS_PERSONAL_WIZARD_STEPS}
            activeIndex={stepIndex}
            onStepClick={handleStepClick}
            form={form}
            instanceId={instanceId}
            getStepStatus={getPersonalStepCompletionStatus}
            stepLabelPrefix="baas.entidades.personal.step"
          />
        )}
      </div>

      <div
        className={`min-w-0 space-y-4 ${isViewOnly ? "pointer-events-none opacity-95" : ""}`}
        aria-readonly={isViewOnly ? "true" : undefined}
        onBeforeInput={isViewOnly ? (event) => event.preventDefault() : undefined}
        onKeyDown={
          isViewOnly
            ? (event) => {
                const target = event.target;
                if (
                  target instanceof HTMLInputElement ||
                  target instanceof HTMLTextAreaElement ||
                  target instanceof HTMLSelectElement
                ) {
                  event.preventDefault();
                }
              }
            : undefined
        }
      >
        {renderStepContent()}
      </div>

      {/* Detail + create: documents (identity accordion, uploads, enviados, verificações) only on Documentos tab */}
      {entityId && !hideDocumentsPanel && currentStep === "document" ? (
        <BaasEntidadeDocumentsPanel
          embedded
          documents={Array.isArray(documents) ? documents : []}
          instance={activeInstance}
          entityId={entityId}
          entityType="pf"
          uploadEnabled={Boolean(documentsUploadEnabled)}
          allowSkipKyc={allowSkipKyc ?? Boolean(documentsUploadEnabled)}
          canEditMeta={isEditableDetail}
          onUploaded={onDocumentsUploaded}
          onDocumentUpdated={onDocumentUpdated}
          verificationEntity={verificationEntity}
          allowPaySmartImageSync={!isCreateFlow}
        />
      ) : null}

      {validationIssues.length > 0 && !isViewOnly ? (
        <div className={formAlertClass} role="alert">
          <p className="font-semibold text-red-900 dark:text-red-50">
            {intl.get("baas.entidades.personal.requiredFieldsTitle")}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-red-800 dark:text-red-100">
            {validationIssues.map((issue) => (
              <li key={`${issue.step}-${issue.errorKey}`}>
                {intl.get(issue.errorKey ?? "baas.entidades.personal.incompleteRequiredFields")}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {error && !isViewOnly ? (
        <div className={formAlertClass} role="alert">
          <p className="whitespace-pre-wrap">{error}</p>
          {existingEntityId && lookupNoticeTone === "warn" ? (
            <button
              type="button"
              className="btn-outline mt-3"
              onClick={() => history.push(buildPersonalEntityDetailPath(existingEntityId))}
            >
              {intl.get("baas.entidades.personal.lookup.openEntity")}
            </button>
          ) : null}
        </div>
      ) : null}
      {saveSuccess ? (
        <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
          {saveSuccess}
        </p>
      ) : null}

      <div className="mt-8 flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-6 dark:border-brand-dark-border">
        {onCancel && !isViewOnly && !isEditableDetail ? (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
            {cancelLabel ?? intl.get("common.cancel")}
          </button>
        ) : null}
        {stepIndex > 0 ? (
          <button type="button" className="btn-secondary" onClick={handleBack} disabled={submitting || lookingUp}>
            {intl.get("baas.entidades.personal.back")}
          </button>
        ) : null}
        {isViewOnly ? (
          !isLastStep ? (
            <button type="button" className="btn-primary" onClick={handleNext}>
              {intl.get("baas.entidades.personal.next")}
            </button>
          ) : null
        ) : isEditableDetail ? (
          <>
            {!isLastStep ? (
              <button
                key="wizard-next"
                type="button"
                className="btn-outline"
                onClick={handleNext}
                disabled={submitting || lookingUp}
              >
                {intl.get("baas.entidades.personal.next")}
              </button>
            ) : null}
            <button
              key="wizard-save"
              type="button"
              className="btn-primary"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting
                ? intl.get("baas.entidades.detail.saving")
                : intl.get("baas.entidades.detail.saveChanges")}
            </button>
          </>
        ) : isLastStep ? (
          <button
            key="wizard-submit"
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting
                ? intl.get("baas.entidades.personal.submitting")
                : intl.get("baas.entidades.personal.submit")}
          </button>
        ) : (
          <button
            key="wizard-next"
            type="button"
            className="btn-primary"
            onClick={() => {
              void handleNext();
            }}
            disabled={createNextDisabled}
            title={
              isCreateFlow && currentStep === "document" && !documentContinueEnabled
                ? intl.get(
                    usePassportDoc
                      ? "baas.entidades.personal.documentRequired"
                      : "baas.entidades.personal.nextDisabledCpf",
                  )
                : undefined
            }
          >
            {lookingUp
              ? intl.get("baas.entidades.personal.lookup.lookingUp")
              : currentStep === "document"
                ? intl.get("baas.entidades.personal.lookupAndContinue")
                : intl.get("baas.entidades.personal.next")}
          </button>
        )}
      </div>

      {!readOnly && !editMode ? (
        <DebugConsoleFooter
          sticky
          expanded={debugExpanded}
          onToggle={() => setDebugExpanded((current) => !current)}
          jsonText={jsonText}
          onJsonChange={(value) => {
            setJsonText(value);
            setDebugError("");
          }}
          onSyncFromForm={handleSyncJsonFromForm}
          onFormatJson={handleFormatJson}
          onRestoreTemplate={handleRestoreTemplate}
          onDebugSubmit={handleDebugSubmit}
          submitting={submitting}
          error={debugError}
          labelNamespace="baas.entidades.personal"
        />
      ) : null}
    </form>
  );
};
